import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useEvent } from '../store';
import { loadEvent as loadEventFromStorage } from '../utils';
import { formatCurrency, formatOdds, calculateWinnings } from '../utils';
import type { Bet } from '../types';

/**
 * User Bets Page
 * Shows all bets made by a user in a specific event
 */
export function UserBetsPage() {
  const { eventId, participantId } = useParams<{ eventId: string; participantId: string }>();
  const navigate = useNavigate();
  const { event, loadEvent, fights } = useEvent();
  
  const [loading, setLoading] = useState(true);
  const [participant, setParticipant] = useState<any>(null);

  useEffect(() => {
    if (!eventId || !participantId) {
      navigate('/leaderboard');
      return;
    }

    const loadEventData = async () => {
      setLoading(true);
      try {
        const eventData = await loadEventFromStorage(eventId);
        if (eventData) {
          loadEvent(eventData);
          const foundParticipant = eventData.participants.find((p: any) => p.id === participantId);
          setParticipant(foundParticipant || null);
        } else {
          navigate('/leaderboard');
        }
      } catch (err) {
        console.error('Error loading event:', err);
        navigate('/leaderboard');
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, [eventId, participantId, loadEvent, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading bets...</p>
        </div>
      </div>
    );
  }

  if (!event || !participant) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <Card padding="lg">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary-text mb-4">Not Found</h1>
            <Button variant="secondary" onClick={() => navigate(`/leaderboard/${eventId}`)}>
              Back to Event Leaderboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const participantBets = participant.bets || [];

  const getFighterOrOutcomeName = (bet: Bet): string => {
    if (bet.outcomeName) return bet.outcomeName;
    const fight = fights.find((f) => f.id === bet.fightId);
    if (!fight) return 'Unknown Fighter';
    const fighter = fight.fighters.find((f) => f.id === bet.fighterId);
    return fighter?.name || 'Unknown Fighter';
  };

  const getFightResult = (fightId: string): string | null => {
    const fight = fights.find((f) => f.id === fightId);
    if (!fight || !fight.result) return null;
    
    const result = fight.result;
    const winner = fight.fighters.find(f => f.id === result.winnerId);
    const winnerName = winner?.name || 'Unknown';
    
    let methodStr = '';
    switch (result.method) {
      case 'ko_tko': methodStr = 'KO/TKO'; break;
      case 'submission': methodStr = 'Sub'; break;
      case 'decision_unanimous': methodStr = 'UD'; break;
      case 'decision_split': methodStr = 'SD'; break;
      case 'decision_majority': methodStr = 'MD'; break;
      case 'dq': methodStr = 'DQ'; break;
      case 'draw': return 'Draw';
      case 'no_contest': return 'No Contest';
      default: methodStr = result.method;
    }
    
    if (result.wentTheDistance) {
      return `${winnerName} by ${methodStr}`;
    } else if (result.round && result.timeInRound) {
      return `${winnerName} by ${methodStr} (R${result.round}, ${result.timeInRound})`;
    }
    return `${winnerName} by ${methodStr}`;
  };

  const statusVariants = {
    pending: 'warning',
    won: 'success',
    lost: 'error',
  } as const;

  const statusLabels = {
    pending: 'Pending',
    won: 'Won',
    lost: 'Lost',
  };

  // Group bets by fight
  const betsByFight = participantBets.reduce((acc: Record<string, Bet[]>, bet: Bet) => {
    if (!acc[bet.fightId]) {
      acc[bet.fightId] = [];
    }
    acc[bet.fightId].push(bet);
    return acc;
  }, {});

  const totalWinnings = participantBets
    .filter((b: Bet) => b.status === 'won' && b.payout)
    .reduce((sum: number, b: Bet) => sum + (b.payout || 0), 0);

  const totalBets = participantBets.length;
  const wonBets = participantBets.filter((b: Bet) => b.status === 'won').length;
  const lostBets = participantBets.filter((b: Bet) => b.status === 'lost').length;
  const pendingBets = participantBets.filter((b: Bet) => b.status === 'pending').length;

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary-text">{participant.name}</h1>
            <p className="text-sm text-secondary-text">{event.name}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/leaderboard/${eventId}`)}
            >
              Back to Event Leaderboard
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/leaderboard')}
            >
              Global Leaderboard
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Summary Stats */}
        <Card padding="md">
          <h2 className="text-lg font-semibold text-primary-text mb-4">Betting Summary</h2>
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
              <div className="text-xs text-secondary-text mb-1">Total Bets</div>
              <div className="text-lg font-bold text-primary-text">{totalBets}</div>
            </div>
            <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
              <div className="text-xs text-secondary-text mb-1">Won</div>
              <div className="text-lg font-bold text-success-green">{wonBets}</div>
            </div>
            <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
              <div className="text-xs text-secondary-text mb-1">Lost</div>
              <div className="text-lg font-bold text-error-red">{lostBets}</div>
            </div>
            <div className="bg-tertiary-bg rounded-lg p-3 border border-border">
              <div className="text-xs text-secondary-text mb-1">Pending</div>
              <div className="text-lg font-bold text-warning-yellow">{pendingBets}</div>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <div className="text-sm text-secondary-text mb-1">Total Winnings</div>
            <div className="text-2xl font-bold text-success-green">
              {formatCurrency(totalWinnings)}
            </div>
          </div>
        </Card>

        {/* Bets by Fight */}
        {Object.keys(betsByFight).length === 0 ? (
          <Card padding="lg">
            <div className="text-center py-8">
              <p className="text-secondary-text">No bets placed yet</p>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {Object.entries(betsByFight).map(([fightId, bets]) => {
              const typedBets = bets as Bet[];
              const fight = fights.find((f) => f.id === fightId);
              const fightName = fight ? fight.fighters.map((f) => f.name).join(' vs ') : 'Unknown Fight';

              return (
                <Card key={fightId} padding="md">
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-primary-text">{fightName}</h3>
                    {fight && (
                      <Badge
                        variant={
                          fight.status === 'completed'
                            ? 'default'
                            : fight.status === 'live'
                            ? 'error'
                            : 'info'
                        }
                        size="sm"
                        className="mt-2"
                      >
                        {fight.status}
                      </Badge>
                    )}
                  </div>

                  <div className="space-y-3">
                    {typedBets.map((bet: Bet) => {
                      const fighterOrOutcomeName = getFighterOrOutcomeName(bet);
                      const potentialProfit = bet.status === 'pending'
                        ? calculateWinnings(bet.amount, bet.odds)
                        : bet.status === 'won' && bet.payout
                        ? calculateWinnings(bet.amount, bet.odds)
                        : null;

                      return (
                        <div
                          key={bet.id}
                          className={`p-4 rounded-lg border-2 ${
                            bet.status === 'won'
                              ? 'border-success-green bg-success-green/5'
                              : bet.status === 'lost'
                              ? 'border-error/30 bg-error/5'
                              : 'border-border bg-tertiary-bg'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="font-semibold text-lg text-primary-text">
                                  {fighterOrOutcomeName}
                                </div>
                                <Badge variant={statusVariants[bet.status]} size="sm">
                                  {statusLabels[bet.status]}
                                </Badge>
                              </div>
                              <div className="text-xs text-muted-text mt-1">
                                Placed: {new Date(bet.placedAt).toLocaleString()}
                              </div>
                              {(bet.status === 'won' || bet.status === 'lost') && getFightResult(bet.fightId) && (
                                <div className="text-xs text-muted-text">
                                  Result: {getFightResult(bet.fightId)}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                            <div>
                              <div className="text-xs text-secondary-text mb-1">Bet Amount</div>
                              <div className="font-semibold text-primary-text">
                                {formatCurrency(bet.amount)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-secondary-text mb-1">Odds</div>
                              <div className="font-semibold text-primary-text">
                                {formatOdds(bet.odds)}
                              </div>
                            </div>
                            {bet.status === 'won' && bet.payout && (
                              <div>
                                <div className="text-xs text-secondary-text mb-1">Payout</div>
                                <div className="font-semibold text-success-green">
                                  {formatCurrency(bet.payout)}
                                </div>
                              </div>
                            )}
                            {bet.status === 'pending' && potentialProfit !== null && (
                              <div>
                                <div className="text-xs text-secondary-text mb-1">Potential Profit</div>
                                <div className="font-semibold text-success-green">
                                  {formatCurrency(potentialProfit)}
                                </div>
                              </div>
                            )}
                            {bet.status === 'lost' && (
                              <div>
                                <div className="text-xs text-secondary-text mb-1">Result</div>
                                <div className="font-semibold text-error-red">
                                  Lost
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Parlay Legs */}
                          {bet.isParlay && bet.parlayLegs && bet.parlayLegs.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-border">
                              <div className="text-sm font-semibold text-primary-text mb-2">Parlay Legs:</div>
                              <div className="space-y-2">
                                {bet.parlayLegs.map((leg, index) => {
                                  const legResult = (leg.status === 'won' || leg.status === 'lost') 
                                    ? getFightResult(leg.fightId) 
                                    : null;
                                  return (
                                    <div 
                                      key={index}
                                      className={`p-2 rounded border ${
                                        leg.status === 'won' 
                                          ? 'bg-success-green/10 border-success-green' 
                                          : leg.status === 'lost' 
                                          ? 'bg-error/10 border-error' 
                                          : 'bg-secondary-bg border-border'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="text-sm font-medium text-primary-text">{leg.fightName}</div>
                                          <div className="text-xs text-secondary-text mt-1">
                                            {leg.selection} ({leg.odds > 0 ? '+' : ''}{leg.odds})
                                          </div>
                                          {legResult && (
                                            <div className="text-xs text-muted-text mt-1">
                                              Result: {legResult}
                                            </div>
                                          )}
                                        </div>
                                        <div className="ml-2">
                                          {leg.status === 'won' && (
                                            <Badge variant="success" size="sm">✓ Won</Badge>
                                          )}
                                          {leg.status === 'lost' && (
                                            <Badge variant="error" size="sm">✗ Lost</Badge>
                                          )}
                                          {leg.status === 'pending' && (
                                            <Badge variant="warning" size="sm">Pending</Badge>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

